// react
import { useMemo } from 'react';

// internationalization
import { useTranslation } from 'react-i18next';

// tanstack
import { useParams } from '@tanstack/react-router';

// material ui icons
import ErrorRoundedIcon from '@mui/icons-material/ErrorRounded';
import WarningRoundedIcon from '@mui/icons-material/WarningRounded';

// material ui
import {
  Avatar,
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material';

// api
import { useGetEnergyGroupQuery } from '@/api/generated/recalc/endpoints/v2/energyGroups';

// shared components
import InsetDrawer from '@/components/ui/drawer/InsetDrawer';

// feature-hooks
import { useEnergyConsumptionCsvDownloader } from '@/features/energyxplorer/hooks/useNodeConsumptionCsvDownload';
import { useEnergyConsumptionExcelDownloader } from '@/features/energyxplorer/hooks/useNodeConsumptionExcelDownload';

// components
import useSearchParams from '@/hooks/useStableSearchParams';
import { getTypeColor, getTypeIcon } from '@/utils/nodeTypeMapper';

// relative
import { useEnergyGroupOpeningHours } from '../../hooks/useEnergyGroupOpeningHours';
import OpeningHoursCard from '../card/OpeningHoursCard';
import AnalysisSelector from './AnalysisSelector';
import { summarizeValidationResult } from './chartRenderes';
import DataStreamDetailList from './DataStreamDetailList.tsx';
import EnergyTreeNodeDetailSummary from './EnergyTreeNodeDetailSummary';
import NodeInitiativeInclusionList from './NodeInitiativeInclusionList.tsx';

interface EnergyTreeNode {
  energyTreeNodeId: string;
  name: string;
  type: string;
  location?: Record<string, unknown>;
  dataStreams?: Array<{ unitId: number }>;
}

interface Unit {
  UnitId: number;
  Name: string;
  [key: string]: unknown;
}

interface ValidationError {
  error_type?: string;
  warning_type?: string;
  message: string;
}

interface ValidationResult {
  [key: string]: unknown;
}

interface InfoNodeDrawerProps {
  open: boolean;
  onClose: () => void;
  units?: Unit[];
  energyTreeNode?: EnergyTreeNode;
  validationResult?: ValidationResult;
}

const InfoNodeDrawer = ({
  open,
  onClose,
  units,
  energyTreeNode,
  validationResult,
}: InfoNodeDrawerProps) => {
  const [searchParams] = useSearchParams();
  const { energyGroupId } = useParams({ strict: false });
  const { t } = useTranslation(['energyxplorer-tree-tab', 'common']);
  const { downloadEnergyCsv } = useEnergyConsumptionCsvDownloader();
  const { downloadEnergyExcel, isDownloading } =
    useEnergyConsumptionExcelDownloader();

  const { data: selectedEnergyGroup } = useGetEnergyGroupQuery(
    { groupId: energyGroupId },
    { skip: !energyGroupId },
  );

  // Get validation summary and check if this node has validation issues
  const validationSummary = validationResult
    ? summarizeValidationResult(validationResult)
    : null;

  const nodeDeviceId = energyTreeNode?.energyTreeNodeId;
  const nodeValidation = validationSummary?.deviceValidationMap?.get(
    nodeDeviceId,
  ) || { errors: [], warnings: [] };
  const hasErrors = nodeValidation.errors.length > 0;
  const hasWarnings = nodeValidation.warnings.length > 0;

  const unitsOnNode = useMemo(() => {
    if (!units || !energyTreeNode?.dataStreams) return [];
    const unitIds = new Set(energyTreeNode.dataStreams.map((ds) => ds.unitId));
    return units.filter((u) => unitIds.has(u.UnitId));
  }, [units, energyTreeNode]);

  const { openingHours } = useEnergyGroupOpeningHours() as {
    openingHours: Record<string, unknown> | null;
  };

  const renderValidationItem = (
    item: ValidationError,
    index: number,
    isError: boolean,
  ) => {
    const itemType = item.error_type || item.warning_type;
    const translatedTitle = itemType
      ? t(`tree_validation.validation_rule.${itemType}.title`, {
          nodeName: energyTreeNode?.name || '',
          defaultValue: item.message,
        })
      : item.message;
    const translatedMessage = itemType
      ? t(`tree_validation.validation_rule.${itemType}.message`, {
          nodeName: energyTreeNode?.name || '',
          defaultValue: item.message,
        })
      : item.message;
    const translatedAction = itemType
      ? t(`tree_validation.validation_rule.${itemType}.action`, {
          nodeName: energyTreeNode?.name || '',
          defaultValue: '',
        })
      : '';

    return (
      <Stack key={index} spacing={0.5}>
        <ListItem sx={{ py: 0.5, px: 0 }}>
          <ListItemIcon sx={{ minWidth: 32 }}>
            {isError ? (
              <ErrorRoundedIcon color="error" fontSize="small" />
            ) : (
              <WarningRoundedIcon color="warning" fontSize="small" />
            )}
          </ListItemIcon>
          <ListItemText
            primary={translatedTitle}
            secondary={translatedMessage}
            slotProps={{
              primary: {
                variant: 'body2',
                fontWeight: 600,
              },

              secondary: {
                variant: 'body2',
              }
            }} />
        </ListItem>
        {translatedAction && (
          <Typography variant="caption" sx={{ ml: 4, fontStyle: 'italic' }}>
            {t('nodes.info_drawer.tip', { ns: 'energyxplorer' })}{' '}
            {translatedAction}
          </Typography>
        )}
      </Stack>
    );
  };

  if (!energyTreeNode) return null;

  return (
    <InsetDrawer
      open={open}
      onClose={onClose}
      title={
        <Stack direction="row" spacing={1} alignItems="center">
          <Avatar
            sx={{
              width: 32,
              height: 32,
              bgcolor: getTypeColor(energyTreeNode.type) || '#1976d2',
              color: 'white',
              fontSize: 16,
            }}
          >
            <i className={`fa-solid ${getTypeIcon(energyTreeNode.type)}`} />
          </Avatar>
          <Typography>
            {energyTreeNode.name || t('node_dialog.info_node_tab.title')}
          </Typography>
        </Stack>
      }
    >
      <Stack spacing={2}>
        <Stack direction="row" spacing={1}>
          <AnalysisSelector
            energyTreeNodeId={energyTreeNode.energyTreeNodeId}
            searchParams={searchParams}
          />
          <Button
            variant="contained"
            startIcon={
              <i
                className="fa-solid fa-file-csv"
                style={{ color: '#2f5aaa' }}
              />
            }
            onClick={() => downloadEnergyCsv(energyTreeNode)}
          >
            {t('node_dialog.info_node_tab.actions.download_csv')}
          </Button>
          <Button
            variant="contained"
            startIcon={
              <i
                className="fa-solid fa-file-excel"
                style={{ color: '#217346' }}
              />
            }
            onClick={() => downloadEnergyExcel(energyTreeNode)}
            disabled={isDownloading}
          >
            {isDownloading ? 'Downloading...' : 'Download Excel'}
          </Button>
        </Stack>

        <EnergyTreeNodeDetailSummary
          key={energyTreeNode.energyTreeNodeId}
          node={energyTreeNode}
        />

        {unitsOnNode.length > 0 && (
          <DataStreamDetailList dataStreams={unitsOnNode} />
        )}

        {selectedEnergyGroup && (
          <NodeInitiativeInclusionList
            clickedNode={{
              energyTreeNodeId: energyTreeNode.energyTreeNodeId,
              type: energyTreeNode.type,
              name: energyTreeNode.name,
            }}
            energyGroup={selectedEnergyGroup}
          />
        )}

        <OpeningHoursCard
          title={t('energyxplorer:tooltips.openingHours')}
          location={energyTreeNode.location || {}}
          description="Åbningstider for denne node"
          openingHours={openingHours}
        />

        {hasErrors && (
          <Stack spacing={1} sx={{ p: 2 }}>
            <List dense sx={{ pt: 0 }}>
              {nodeValidation.errors.map(
                (error: ValidationError, index: number) =>
                  renderValidationItem(error, index, true),
              )}
            </List>
          </Stack>
        )}

        {hasWarnings && (
          <Stack spacing={1} sx={{ p: 2 }}>
            <List dense sx={{ pt: 0 }}>
              {nodeValidation.warnings.map(
                (warning: ValidationError, index: number) =>
                  renderValidationItem(warning, index, false),
              )}
            </List>
          </Stack>
        )}
      </Stack>
    </InsetDrawer>
  );
};

export default InfoNodeDrawer;
